import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Pill } from "@/components/perf";
import { CalendarRange, Wand2, Send, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const DIVISIONS = ["Low Intermediate", "Intermediate", "High Intermediate", "Advanced", "Pro"];

export default function LeagueScheduler() {
  const [division, setDivision] = useState("Low Intermediate");
  const [weeks, setWeeks] = useState(6);
  const [sched, setSched] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (d) => {
    const { data } = await api.get("/admin/league/schedule", { params: { division: d } });
    setSched(data);
  };
  useEffect(() => { load(division); }, [division]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/league/schedule/generate", { division, weeks: Number(weeks) });
      setSched(data);
      toast.success("AI schedule generated — review and publish when ready");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    if (!window.confirm(`Publish this ${sched.weeks}-week ${sched.division} schedule? Matches become visible to members on the League tab.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/league/schedule/${sched.id}/publish`);
      toast.success(`Published — ${data.matches_created} matches created`);
      await load(division);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const discard = async () => {
    if (!window.confirm("Discard this draft schedule?")) return;
    await api.delete(`/admin/league/schedule/${sched.id}`);
    setSched(null);
    toast.success("Draft discarded");
  };

  return (
    <div className="mt-8" data-testid="league-scheduler">
      <h3 className="font-display font-bold text-2xl uppercase mb-1 flex items-center gap-2">
        <CalendarRange className="w-6 h-6 text-pb-red" /> AI League Schedule
      </h3>
      <p className="text-sm text-zinc-500 mb-5">Pick a division and season length — the AI builds a balanced week-by-week round-robin chart from your current teams. Publish to create the official matches members see on the League tab.</p>

      <div className="bg-pb-surface border border-pb-border p-5">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Division</label>
            <select data-testid="schedule-division" value={division} onChange={(e) => setDivision(e.target.value)}
              className="bg-pb-elevated border border-pb-border px-3 py-2 text-white focus:outline-none focus:border-pb-red">
              {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Weeks</label>
            <input data-testid="schedule-weeks" type="number" min={1} max={16} value={weeks} onChange={(e) => setWeeks(e.target.value)}
              className="w-24 bg-pb-elevated border border-pb-border px-3 py-2 text-white focus:outline-none focus:border-pb-red" />
          </div>
          <button data-testid="generate-schedule-btn" onClick={generate} disabled={busy}
            className="flex items-center gap-2 bg-pb-red text-black font-bold uppercase tracking-widest text-sm px-5 py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
            <Wand2 className="w-4 h-4" /> {busy ? "Working…" : "Generate AI Schedule"}
          </button>
        </div>

        {sched && (
          <div data-testid="schedule-chart">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="font-display font-bold uppercase">{sched.division} · {sched.weeks} weeks</span>
              <Pill color={sched.status === "published" ? "#4ADE80" : "#F59E0B"}>{sched.status}</Pill>
              {sched.status === "draft" && (
                <span className="flex gap-2 ml-auto">
                  <button data-testid="publish-schedule-btn" onClick={publish} disabled={busy}
                    className="flex items-center gap-1.5 bg-pb-cyan text-black font-bold uppercase tracking-widest px-4 py-2 text-xs hover:bg-[#86EFAC] active:scale-95 transition-all disabled:opacity-50">
                    <Send className="w-3.5 h-3.5" /> Publish to League
                  </button>
                  <button data-testid="discard-schedule-btn" onClick={discard}
                    className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-red-500 hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </span>
              )}
            </div>
            {sched.insight && (
              <div className="flex items-start gap-2 bg-pb-elevated border border-pb-cyan/30 p-3 text-xs text-zinc-300 mb-4">
                <Sparkles className="w-4 h-4 text-pb-cyan mt-0.5 shrink-0" />
                <span><span className="text-pb-cyan font-bold uppercase tracking-widest text-[10px]">AI Insight · </span>{sched.insight}</span>
              </div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {sched.chart.map((wk) => (
                <div key={wk.week} data-testid={`schedule-week-${wk.week}`} className="bg-pb-elevated border border-pb-border p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-display font-black uppercase text-pb-red">Week {wk.week}</span>
                    <span className="text-[10px] font-mono text-zinc-500">{wk.date}</span>
                  </div>
                  <div className="space-y-1.5">
                    {wk.matchups.map((m, i) => (
                      <div key={i} className="text-xs text-zinc-300 border-b border-pb-border/40 pb-1.5 last:border-0">
                        <span className="font-semibold">{m.team_a_name}</span>
                        <span className="text-zinc-600 mx-1.5">vs</span>
                        <span className="font-semibold">{m.team_b_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
